import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';
import { toast } from 'sonner';

import { useAuth } from '@/features/auth/contexts/auth.context';
import LoadingScreen from '@/features/common/pages/LoadingScreen';

interface AuthGuardProps {
  children: React.ReactNode;
  redirectTo?: string;
  errorMessage?: string;
  /** Override authentication check — e.g. allow passage when user exists in UserContext even without a BetterAuth session. */
  isAuthenticatedOverride?: boolean;
}

export function AuthGuard({
  children,
  redirectTo = '/onboarding',
  errorMessage,
  isAuthenticatedOverride,
}: AuthGuardProps) {
  const { t } = useTranslation();
  const { isAuthenticated: hasSession, isLoading: isAuthLoading } = useAuth();
  const hasShownAuthErrorRef = useRef(false);
  const resolvedErrorMessage = errorMessage ?? t('errors.authentication_error');

  const isAuthenticated = isAuthenticatedOverride ?? hasSession;

  useEffect(() => {
    if (isAuthLoading) return;
    if (isAuthenticated) return;
    if (hasShownAuthErrorRef.current) return;

    hasShownAuthErrorRef.current = true;
    toast.error(resolvedErrorMessage, {
      style: {
        background: '#fef2f2',
        border: '1px solid #dc2626',
        color: '#991b1b',
      },
    });
  }, [resolvedErrorMessage, isAuthLoading, isAuthenticated]);

  if (isAuthLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
